import {
  FunctionComponent,
  KeyboardEvent,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { isCellEditable } from '../utils/ultratable.utils';
import { GeneralFunctionType } from '../types/functions.types';
import { Getter } from '@tanstack/react-table';
import { PortalUpdatePosition } from '../../../pages/manco/positionUpdatePortal/utils/buildFns';
import { Column } from '@tanstack/react-table';
import { EditableFieldsProperties } from '../types/editable.types';
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaChevronDown,
  FaCheck,
} from 'react-icons/fa';
import { ImSpinner8 } from 'react-icons/im';
import ErrorMessage from '../../../ui/ErrorMessage';
import { NumericFormat } from 'react-number-format';
import { useUpdatePositionData } from '../../../pages/manco/positionUpdatePortal/services/mutations';
import { NESTED_KEY_REGEX } from '../../../pages/manco/positionUpdatePortal/PositionUpdatePortal.page';
import { Listbox } from '@headlessui/react';
import { useMutationState } from '@tanstack/react-query';

const NONE_LABEL = 'None';
const NONE_VALUE_CLASS = 'italic text-gray-400';

function isNoneValue(val: unknown): boolean {
  return val === null || val === undefined || val === '';
}

function getOptionDisplayLabel(val: unknown, allowNone: boolean): string {
  if (isNoneValue(val) && allowNone) return NONE_LABEL;
  return String(val ?? '');
}

interface EditableCellProps {
  id: string;
  getValue: Getter<unknown>;
  original: PortalUpdatePosition;
  column: Column<PortalUpdatePosition, unknown>;
  renderForm: (
    value: any,
    editOptions: EditableFieldsProperties,
    original: PortalUpdatePosition,
    id: string,
  ) => void;
}

const EditableCell: FunctionComponent<EditableCellProps> = ({
  id,
  original,
  column,
  getValue,
  renderForm,
}) => {
  const isEditable = isCellEditable(original, column);
  const value = getValue();

  const editOptions: EditableFieldsProperties = original.editable_fields.find(
    (field: any) => field.key_name === column.id,
  )!;

  const nestedKeyID = column.id.replace(NESTED_KEY_REGEX, '.');
  const showChange = original?.cdum_changed_paths?.some(
    (pathObj: any) =>
      pathObj.key_path.trim().toLowerCase() ===
      nestedKeyID.trim().toLowerCase(),
  );

  // Use useMutationState to track if this cell is being updated
  const mutations = useMutationState({
    filters: {
      mutationKey: ['position-update', id],
    },
  });

  const isUpdating = mutations.some(
    (mutation) => mutation.status === 'pending',
  );

  return isEditable ? (
    <div
      onClick={() =>
        !isUpdating && renderForm(value, editOptions, original, id)
      }
      className={`flex h-full w-full items-center justify-center border-none px-2.5 ${isUpdating ? 'cursor-not-allowed opacity-50' : 'hover:cursor-pointer hover:outline hover:outline-[1.25px] hover:outline-blue-500 focus:cursor-pointer focus:outline focus:outline-[1.25px] focus:outline-blue-500'} ${showChange ? 'bg-orange-200 hover:bg-orange-300 focus:bg-orange-300' : 'bg-[var(--pastle-blue-lighter)] hover:bg-[var(--pastle-blue-light)] focus:bg-[var(--pastle-blue-light)]'}`}
    >
      {isUpdating ? (
        <ImSpinner8 className="h-4 w-4 animate-spin text-gray-500" />
      ) : (
        (value as string)
      )}
    </div>
  ) : (
    <span className="flex h-full items-center justify-center">
      {value as string}
    </span>
  );
};

export default EditableCell;

interface GenericPortalUpdateFormProps {
  editOptions: EditableFieldsProperties;
  value: unknown;
  callBackEditFn: GeneralFunctionType;
  original: PortalUpdatePosition;
  id: string;
  onClose: () => void;
}

export const GenericPortalUpdateForm: FunctionComponent<
  GenericPortalUpdateFormProps
> = ({
  editOptions,
  value: initialValue,
  callBackEditFn,
  original,
  id,
  onClose,
}) => {
  const updatePositionData = useUpdatePositionData(id);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentInitialValue, setCurrentInitialValue] = useState(initialValue);
  const currentValueRef = useRef<string | number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: { value: currentInitialValue as string | number },
    mode: 'onChange',
  });

  const currentValue = watch('value');
  currentValueRef.current = currentValue;
  const isUpdated = String(currentValue) !== String(currentInitialValue);

  useEffect(() => {
    if (updatePositionData.isSuccess) {
      setShowSuccess(true);
      setCurrentInitialValue(currentValueRef.current);
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [updatePositionData.isSuccess]);

  const {
    raptor_type: type,
    validation,
    placeholder_value: placeholder,
    allow_none,
  } = editOptions;

  const handleKey = async (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter') {
      const valid = await trigger('value');
      if (valid) {
        handleSubmit(onSubmit)();
      }
    }
    if (e.key === 'Escape') {
      reset();
    }
  };

  const onSubmit = async (data: { value: any }) => {
    const propertyID = editOptions.key_name;
    await callBackEditFn(
      original,
      propertyID,
      editOptions.column_name,
      data.value,
      updatePositionData,
    );
  };

  const getStatusIcon = () => {
    if (showSuccess) return <FaCheckCircle className="ml-2 text-green-500" />;
    if (updatePositionData.isError)
      return <FaExclamationCircle className="ml-2 text-red-500" />;
    return null;
  };

  const getInputClass = () => {
    if (errors.value) {
      return 'border-red-500 bg-red-50 text-red-700 ring-1 ring-red-500';
    }
    if (isUpdated) {
      return 'border-green-500 bg-green-50 text-green-700 ring-1 ring-green-500';
    }
    return 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
  };

  const getButtonClass = () => {
    if (showSuccess) return 'bg-green-600 hover:bg-green-700';
    if (updatePositionData.isError) return 'bg-red-600 hover:bg-red-700';
    return 'bg-mainblue hover:bg-mainblue/90 disabled:bg-gray-300';
  };

  const getButtonText = () => {
    if (showSuccess) return 'Success!';
    if (updatePositionData.isError) return 'Error — Retry';
    return 'Submit Changes';
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="relative flex flex-col gap-6"
    >
      {/* Context Header with Current Value */}
      <div className="rounded-lg bg-gray-50 p-4">
        <div className="flex flex-col">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-mainblue">
              {editOptions.column_name}
            </h3>
            <div className="flex items-center gap-2">
              {type === 'string' ? (
                validation?.values && validation.values.length > 0 ? (
                  <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                    Select Field
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                    Text Field
                  </span>
                )
              ) : (
                <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-700/10">
                  Numeric Field
                </span>
              )}
            </div>
          </div>

          {/* Metadata row with border */}
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Current Value:</span>
              <div className="inline-flex min-w-[100px] items-center rounded-md bg-white px-3 py-1 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200">
                {currentInitialValue !== undefined &&
                currentInitialValue !== null ? (
                  String(currentInitialValue)
                ) : (
                  <span className="italic text-gray-400">None</span>
                )}
              </div>
            </div>
          </div>

          {/* Fund and Position Info */}
          <div className="mt-2 flex flex-col gap-4 border-t border-gray-200 pt-4 text-sm">
            <div className="flex max-w-[500px] gap-4">
              <div className="flex-1">
                <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Fund
                </span>
                <p className="mt-1 font-medium text-gray-900">
                  {original.fund_name}
                </p>
              </div>
              <div className="flex-1">
                <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Asset Name
                </span>
                <p className="mt-1 font-medium text-gray-900">
                  {original.gm_name_long}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Value Card */}
      <div className="rounded-lg border border-gray-200 px-4 py-2">
        <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
          New Value
        </span>
        <div className="mt-2">
          {type === 'string' ? (
            !validation?.values || validation.values.length === 0 ? (
              <input
                {...register('value', {
                  ...(allow_none ? {} : { required: 'This field is required' }),
                })}
                onKeyUp={handleKey}
                disabled={updatePositionData.isPending}
                placeholder={placeholder}
                className={`w-full rounded-md border px-3 py-2 shadow-sm disabled:cursor-not-allowed disabled:bg-gray-100 ${getInputClass()}`}
              />
            ) : (
              <Listbox
                value={currentValue}
                onChange={(value) => {
                  setValue('value', value);
                }}
                disabled={updatePositionData.isPending}
              >
                <div className="relative">
                  <Listbox.Button
                    className={`w-full rounded-md border px-3 py-2 text-left shadow-sm disabled:cursor-not-allowed disabled:bg-gray-100 ${getInputClass()}`}
                  >
                    <span className="block truncate">
                      {allow_none &&
                      validation?.values?.some(isNoneValue) &&
                      isNoneValue(currentValue)
                        ? <span className={NONE_VALUE_CLASS}>{NONE_LABEL}</span>
                        : !isNoneValue(currentValue)
                          ? String(currentValue)
                          : placeholder || 'Select a value'}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <FaChevronDown className="h-3.5 w-3.5 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {[...validation.values]
                      .sort((a, b) => {
                        const aNone = isNoneValue(a);
                        const bNone = isNoneValue(b);
                        if (aNone && !bNone) return -1;
                        if (!aNone && bNone) return 1;
                        return 0;
                      })
                      .map((val) => (
                      <Listbox.Option
                        key={isNoneValue(val) ? '__none__' : String(val)}
                        value={val}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active
                              ? 'bg-blue-100 text-blue-900'
                              : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${selected ? 'font-medium' : 'font-normal'} ${validation?.values?.some(isNoneValue) && isNoneValue(val) && allow_none ? NONE_VALUE_CLASS : ''}`}
                            >
                              {getOptionDisplayLabel(val, allow_none)}
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <FaCheck className="h-3.5 w-3.5" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
            )
          ) : (
            <>
              <Controller
                name="value"
                control={control}
                rules={{
                  ...(allow_none ? {} : { required: 'This field is required' }),
                  validate: (val) => {
                    if (
                      (val === null || val === undefined || val === '') &&
                      allow_none
                    ) {
                      return true;
                    }
                    const num = typeof val === 'string' ? parseFloat(val) : val;
                    if (validation?.min !== undefined && num < validation.min) {
                      return `Minimum value is ${validation.min}`;
                    }
                    if (validation?.max !== undefined && num > validation.max) {
                      return `Maximum value is ${validation.max}`;
                    }
                    return true;
                  },
                }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <NumericFormat
                    value={value ?? ''}
                    onValueChange={(values) =>
                      onChange(values.floatValue ?? (allow_none ? null : ''))
                    }
                    onBlur={onBlur}
                    getInputRef={ref}
                    thousandSeparator
                    disabled={updatePositionData.isPending}
                    placeholder={placeholder}
                    onKeyUp={handleKey}
                    className={`w-full rounded-md border px-3 py-2 shadow-sm disabled:cursor-not-allowed disabled:bg-gray-100 ${getInputClass()}`}
                  />
                )}
              />
              {/* Only show min/max for numeric inputs */}
              {validation &&
                type !== 'string' &&
                (validation.min !== undefined ||
                  validation.max !== undefined) && (
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex gap-2 text-xs text-gray-500">
                      {validation.min !== undefined && (
                        <span>Min: {validation.min}</span>
                      )}
                      {validation.max !== undefined && (
                        <span>Max: {validation.max}</span>
                      )}
                    </div>
                    {errors.value?.message && (
                      <ErrorMessage message={errors.value.message} />
                    )}
                  </div>
                )}
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 border-gray-200 pt-4">
        {getStatusIcon()}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={updatePositionData.isPending}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              updatePositionData.isPending || !!errors?.value || !isUpdated
            }
            className={`flex w-[160px] items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm disabled:cursor-not-allowed ${getButtonClass()}`}
          >
            {updatePositionData.isPending ? (
              <ImSpinner8 className="h-3 w-3 animate-spin" />
            ) : (
              getButtonText()
            )}
          </button>
        </div>
      </div>
    </form>
  );
};
